import { useCallback, useEffect } from 'react';
import { SimpleGrid, Stack } from '@mantine/core';

import { Button, Textarea } from 'components';
import { CreditProfessionalSituation, CreditRequestEmploymentResponse, CreditRequestStep, CreditResponse, EmploymentType, IncomeType, TravelingType, ValidationRules } from 'types';
import { AppConstants } from '../../../../constants';
import CreditRequestEmployment from './income/employment';
import CreditRequestIncome from './income/income';
import { useForm } from '@mantine/form';
import { AppUtils } from 'utils';
import { trackCustomEvent } from 'utils/metaPixel';

const { CREDIT_REQUEST_FORM_PADDING } = AppConstants;

interface EmploymentObject {
  id?: number;
  creditRequestId?: number;
  professionalSituation?: CreditProfessionalSituation;
  salary?: number;
  thirteenthSalary?: boolean;
  bonusReceived?: boolean;
  socialHelp?: boolean;
  employmentType?: EmploymentType;
  contractStartDate?: Date;
  contractEndDate?: Date;
  travelingToWork?: TravelingType;
  partner?: boolean;
}

interface IncomeObject {
  id?: number;
  creditRequestId?: number;
  incomeType?: IncomeType;
  startDate?: Date;
  amount?: number;
  partner?: boolean;
}

const tempEmploymentObject = {
  bonusReceived: undefined,
  contractEndDate: undefined,
  contractStartDate: undefined,
  creditRequestId: undefined,
  employmentType: undefined,
  id: undefined,
  partner: false,
  professionalSituation: undefined,
  requesterPaysSalary: undefined,
  salary: undefined,
  socialHelp: undefined,
  thirteenthPension: undefined,
  thirteenthSalary: undefined,
  travelingToWork: undefined,
} as EmploymentObject;

const tempIncomeObject = {
  creditRequestId: undefined,
  incomeType: undefined,
  startDate: undefined,
  amount: undefined,
  partner: false,
} as IncomeObject;

const employmentsPropertyName = 'employments';
const incomesPropertyName = 'incomes';

const IncomeInformationStep = ({
  form,
  isEditable,
  onSubmit,
  options,
  t,
}: CreditRequestStep) => {
  useEffect(() => {
    trackCustomEvent("ApplicationProgress");
  }, [])


  const onSubmitWrapper = useCallback(() => {
    if (isEditable && !isFormValid()) {
      return;
    }
    onSubmit();
  }, [isEditable, form.values, options]);

  const isFormValid = useCallback(() => {
    let isValid = true;
    if (userEmploymentForm.values) {
      const runEmploymentValidation = (name: keyof CreditResponse | any, value: any, rules: ValidationRules) => {
        const result = AppUtils.validateField(name, value, rules, form, t);
        if (!result) {
          isValid = false;
        }
      };
      Object.keys(userEmploymentForm.values).forEach((_, keyIndex) => {
        const item = userEmploymentForm.values[keyIndex];
        const professionalSituation = item.professionalSituation;
        runEmploymentValidation(`employments.${keyIndex}.professionalSituation`, professionalSituation, { required: true })
        runEmploymentValidation(`employments.${keyIndex}.salary`, item.salary, { required: true })
        if (professionalSituation) {
          if (['EMPLOYED', 'AGENCY'].includes(professionalSituation)) {
            runEmploymentValidation(`employments.${keyIndex}.contractStartDate`, item.contractStartDate, { required: true })
          }
        }
      })
    }
    if (form.values.partnerIncludeInApplication && partnerEmploymentForm.values) {
      const runPartnerEmploymentValidation = (name: keyof CreditRequestEmploymentResponse | any, value: any, rules: ValidationRules) => {
        const result = AppUtils.validateField(name, value, rules, partnerEmploymentForm, t);
        if (!result) {
          isValid = false;
        }
      };
      Object.keys(partnerEmploymentForm.values).forEach((_, keyIndex) => {
        const item = partnerEmploymentForm.values[keyIndex];
        const professionalSituation = item.professionalSituation;
        runPartnerEmploymentValidation('professionalSituation', professionalSituation, { required: true })
        runPartnerEmploymentValidation('salary', item.salary, { required: true })
        if (professionalSituation) {
          if (['EMPLOYED', 'AGENCY'].includes(professionalSituation)) {
            runPartnerEmploymentValidation('contractStartDate', item.contractStartDate, { required: true })
          }
        }
      })
    }
    return isValid;
  }, [form.values, options]);


  const getInitialEmploymentsData = useCallback((partner: boolean) => {
    let array: EmploymentObject[] = [];
    if (!form.values[employmentsPropertyName] || form.values[employmentsPropertyName].length === 0) {
      array.push({ ...tempEmploymentObject, creditRequestId: form.values.id, partner: partner, thirteenthSalary: false });
    } else {
      const employments = form.values[employmentsPropertyName];
      if (employments.length > 0) {
        array = employments.filter(el => el.partner === partner).map((el: EmploymentObject) => ({
          ...el,
          contractStartDate: el.contractStartDate ? new Date(el.contractStartDate) : undefined,
          contractEndDate: el.contractEndDate ? new Date(el.contractEndDate) : undefined,
        }));
      } else {
        array.push({ ...tempEmploymentObject, creditRequestId: form.values.id, partner: partner, thirteenthSalary: false });
      }
    }
    if (array.length === 0) {
      array.push({ ...tempEmploymentObject, creditRequestId: form.values.id, partner: partner, thirteenthSalary: false });
    }
    array.sort((a: EmploymentObject, b: EmploymentObject) => (a.id ?? 0) - (b.id ?? 0));
    return array;
  }, []);

  const getInitialIncomesData = useCallback((partner: boolean) => {
    let array: IncomeObject[] = [];
    if (!form.values[incomesPropertyName] || form.values[incomesPropertyName].length === 0) {
      array.push({ ...tempIncomeObject, creditRequestId: form.values.id, partner: partner });
    } else {
      const incomes = form.values[incomesPropertyName];
      if (incomes.length > 0) {
        array = incomes.filter(el => el.partner === partner).map((el: IncomeObject) => ({
          ...el,
          startDate: el.startDate ? new Date(el.startDate) : undefined,
        }));
      } else {
        array.push({ ...tempIncomeObject, creditRequestId: form.values.id, partner: partner });
      }
    }
    if (array.length === 0) {
      array.push({ ...tempIncomeObject, creditRequestId: form.values.id, partner: partner });
    }
    array.sort((a: IncomeObject, b: IncomeObject) => (a.id ?? 0) - (b.id ?? 0));
    return array;
  }, []);

  const updateCreditEmployments = useCallback(async (array: any, partner: boolean) => {
    let tempArray: any[] = [];
    Object.keys(array).forEach(key => {
      let item: CreditRequestEmploymentResponse = { ...array[key] };
      const isNotEmpty = item.professionalSituation
        || item.salary
        || item.thirteenthSalary
        || item.bonusReceived
        || item.socialHelp
        || item.employmentType
        || item.contractStartDate
        || item.contractEndDate
        || item.travelingToWork;
      if (isNotEmpty) {
        tempArray.push(item);
      }
    });
    let newValue: any[] = [];
    if (partner) {
      newValue = [...Object.values(userEmploymentForm.getValues()), ...tempArray];
    } else {
      newValue = [...tempArray, ...Object.values(partnerEmploymentForm.getValues())];
    }
    form.setFieldValue(employmentsPropertyName, newValue);
  }, [form.values[employmentsPropertyName]]);

  const updateCreditIncomes = useCallback(async (array: any, partner: boolean) => {
    let tempArray: any[] = [];
    Object.keys(array).forEach(key => {
      let item: IncomeObject = { ...array[key] };
      const isNotEmpty = item.incomeType
        || item.startDate
        || item.amount;
      if (isNotEmpty) {
        tempArray.push(item);
      }
    });
    let newValue: any[] = [];
    if (partner) {
      newValue = [...Object.values(userIncomeForm.getValues()), ...tempArray];
    } else {
      newValue = [...tempArray, ...Object.values(partnerIncomeForm.getValues())];
    }
    form.setFieldValue(incomesPropertyName, newValue);
  }, [form.values[incomesPropertyName]]);

  const userEmploymentForm = useForm({
    initialValues: getInitialEmploymentsData(false) as EmploymentObject[],
    onValuesChange(values, _previous) {
      updateCreditEmployments(values, false);
    },
  });

  const partnerEmploymentForm = useForm({
    initialValues: getInitialEmploymentsData(true) as EmploymentObject[],
    onValuesChange(values, _previous) {
      updateCreditEmployments(values, true);
    },
  });

  const userIncomeForm = useForm({
    initialValues: getInitialIncomesData(false) as IncomeObject[],
    onValuesChange(values, _previous) {
      if (values.length === 1 || values[values.length - 1] !== tempIncomeObject) {
        updateCreditIncomes(values, false);
      }
    },
  });

  const partnerIncomeForm = useForm({
    initialValues: getInitialIncomesData(true) as IncomeObject[],
    onValuesChange(values, _previous) {
      if (values.length === 1 || values[values.length - 1] !== tempIncomeObject) {
        updateCreditIncomes(values, true);
      }
    },
  });

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }}>
      <Stack pb={CREDIT_REQUEST_FORM_PADDING} px={CREDIT_REQUEST_FORM_PADDING}>
        <CreditRequestEmployment
          form={form}
          userEmploymentForm={userEmploymentForm}
          partnerEmploymentForm={partnerEmploymentForm}
          options={options}
          t={t}
          disabled={!isEditable}
          tempEmploymentObject={tempEmploymentObject}
          partner={false}
        />
        <CreditRequestIncome
          form={form}
          userIncomeForm={userIncomeForm}
          partnerIncomeForm={partnerIncomeForm}
          options={options}
          t={t}
          disabled={!isEditable}
          tempIncomeObject={tempIncomeObject}
          partner={false}
        />
      </Stack>
      <Stack pb={CREDIT_REQUEST_FORM_PADDING} px={CREDIT_REQUEST_FORM_PADDING}>
        {form.values.partnerIncludeInApplication ?
          <>
            <CreditRequestEmployment
              form={form}
              userEmploymentForm={userEmploymentForm}
              partnerEmploymentForm={partnerEmploymentForm}
              options={options}
              t={t}
              disabled={!isEditable}
              tempEmploymentObject={tempEmploymentObject}
              partner={true}
            />
            <CreditRequestIncome
              form={form}
              userIncomeForm={userIncomeForm}
              partnerIncomeForm={partnerIncomeForm}
              options={options}
              t={t}
              disabled={!isEditable}
              tempIncomeObject={tempIncomeObject}
              partner={true}
            />
          </>
        : undefined}
        <Textarea
          label={t('input.comment.label')}
          key={form.key('comment3')}
          {...form.getInputProps('comment3')}
          info={t('input.comment.info.message')}
          disabled={!isEditable}
        />
        <Button onClick={onSubmitWrapper}>
          {t('action.next')}
        </Button>
      </Stack>
    </SimpleGrid>
  )
}

export default IncomeInformationStep;